import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Popover, Stack, TextField, Tooltip, Typography } from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import { FeedbackRecord } from '../services/learningApi';
import { useApis, useLearningStatus } from './learning/common';

interface Props {
  sourceType: 'function_run' | 'chain_execution';
  sourceId: string;
  /** Called after the user's thumbs are saved or removed. */
  onChange?: () => void;
}

/** Thumbs up/down on a run. Feeds self-improvement; hidden unless the feature is enabled for the account. */
export const RunFeedbackButtons: React.FC<Props> = ({ sourceType, sourceId, onChange }) => {
  const status = useLearningStatus();
  const apis = useApis();
  const [mine, setMine] = useState<FeedbackRecord | null>(null);
  const [anchor, setAnchor] = useState<{ el: HTMLElement; label: 'thumbs_up' | 'thumbs_down' } | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const enabled = !!status?.enabled;

  useEffect(() => {
    if (!enabled || !sourceId) return;
    apis.feedback
      .list(sourceType, sourceId)
      .then((r) => setMine(r.feedback.find((f) => (f.label === 'thumbs_up' || f.label === 'thumbs_down') && f.created_by) || null))
      .catch(() => undefined);
  }, [apis, enabled, sourceType, sourceId]);

  if (!enabled) return null;

  const submit = async () => {
    if (!anchor) return;
    setBusy(true);
    setError(null);
    try {
      if (mine) await apis.feedback.remove(mine.id);
      const created = await apis.feedback.create({ source_type: sourceType, source_id: sourceId, label: anchor.label, note: note.trim() || undefined });
      setMine(created);
      setAnchor(null);
      setNote('');
      onChange?.();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (!mine) return;
    setBusy(true);
    try {
      await apis.feedback.remove(mine.id);
      setMine(null);
      onChange?.();
    } finally {
      setBusy(false);
    }
  };

  const click = (label: 'thumbs_up' | 'thumbs_down') => (e: React.MouseEvent<HTMLElement>) => {
    if (mine?.label === label) {
      clear();
      return;
    }
    setNote('');
    setAnchor({ el: e.currentTarget, label });
  };

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
        Was this good?
      </Typography>
      <Tooltip title={mine?.label === 'thumbs_up' ? 'Remove' : 'Good result'}>
        <span>
          <IconButton size="small" disabled={busy} onClick={click('thumbs_up')} color={mine?.label === 'thumbs_up' ? 'success' : 'default'}>
            {mine?.label === 'thumbs_up' ? <ThumbUpIcon fontSize="small" /> : <ThumbUpOutlinedIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={mine?.label === 'thumbs_down' ? 'Remove' : 'Bad result'}>
        <span>
          <IconButton size="small" disabled={busy} onClick={click('thumbs_down')} color={mine?.label === 'thumbs_down' ? 'error' : 'default'}>
            {mine?.label === 'thumbs_down' ? <ThumbDownIcon fontSize="small" /> : <ThumbDownOutlinedIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      <Popover
        open={!!anchor}
        anchorEl={anchor?.el}
        onClose={() => !busy && setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Stack spacing={1.5} sx={{ p: 2, width: 320 }}>
          <Typography variant="subtitle2">{anchor?.label === 'thumbs_up' ? 'What was good?' : 'What went wrong?'} (optional)</Typography>
          <TextField
            autoFocus
            multiline
            minRows={2}
            size="small"
            placeholder={anchor?.label === 'thumbs_up' ? 'e.g. Substitutes were spot on' : 'e.g. Suggested a different brand of baby formula'}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          {error && (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          )}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" onClick={() => setAnchor(null)} disabled={busy}>
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={submit} disabled={busy}>
              Send
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Used to improve this {sourceType === 'chain_execution' ? 'chain' : 'function'} when self-improvement is on.
          </Typography>
        </Stack>
      </Popover>
    </Box>
  );
};

export default RunFeedbackButtons;
